import { useState } from 'react';
import { ImageBackground, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/components/layout';
import { Button, Container, Pill, Text } from '@/components/ui';
import { TeamGrid } from '@/components/marketing';
import { SeoHead } from '@/components/SeoHead';
import {
  useAboutStats,
  usePhilosophie,
  useTimeline,
  useVerein,
  type PhilosophiePillar,
} from '@/content/about';
import { useTeachers } from '@/content/team';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

const webTransition = (props = 'background-color, color, transform, border-color', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

export default function AboutScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const stats = useAboutStats();
  const timeline = useTimeline();
  const philosophie = usePhilosophie();
  const verein = useVerein();
  const teachers = useTeachers();

  return (
    <View>
      <SeoHead
        title={t('about.seo.title')}
        description={t('about.seo.description')}
      />

      {/* Pagehead with stats */}
      <View style={{ paddingTop: 48, paddingBottom: 64 }}>
        <Container>
          <View style={st.eyebrowRow}>
            <View style={[st.dot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text variant="meta" tone="fgMute">{t('about.hero.eyebrow')}</Text>
          </View>
          <View style={st.headGrid}>
            <View style={{ flex: 1.5, minWidth: 320 }}>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: fluidFontSize(40, 9, 88) as any,
                  lineHeight: fluidLineHeight(0.95, 84) as any,
                  color: theme.colors.fg,
                  letterSpacing: -2,
                }}
              >
                {t('about.hero.titlePart1')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: fluidFontSize(40, 9, 88) as any }}>
                  {t('about.hero.titleItalic1')}
                </Text>
                {t('about.hero.titlePart2')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accentAlt, fontSize: fluidFontSize(40, 9, 88) as any }}>
                  {t('about.hero.titleItalic2')}
                </Text>
              </Text>
              <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 24, maxWidth: 580 }}>
                {t('about.hero.intro')}
              </Text>
            </View>
            <HeadPhotoCard theme={theme} />
          </View>

          <View style={[st.statRow, { borderTopColor: theme.colors.rule, borderBottomColor: theme.colors.rule }]}>
            {stats.map((s, i, arr) => (
              <View
                key={s.label}
                style={[
                  st.stat,
                  i < arr.length - 1 && { borderRightColor: theme.colors.rule, borderRightWidth: 1 },
                ]}
              >
                <Text variant="eyebrow" tone="fgMute">{s.label}</Text>
                <Text
                  italic
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: 44,
                    lineHeight: 48,
                    color: theme.colors.accent,
                    marginTop: 6,
                  }}
                >
                  {s.value}
                </Text>
              </View>
            ))}
          </View>
        </Container>
      </View>

      {/* 02 Geschichte */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('about.geschichte.idx')}
            title={t('about.geschichte.title')}
            italic={t('about.geschichte.italic')}
            sub={t('about.geschichte.sub')}
          />
          <View style={[st.timeline, { borderLeftColor: theme.colors.rule }]}>
            {timeline.map((e) => (
              <View key={e.year} style={st.tlRow}>
                <View
                  style={[
                    st.tlBullet,
                    {
                      backgroundColor: e.current ? theme.colors.accentAlt : theme.colors.fg,
                      borderColor: theme.colors.bg,
                      width: e.current ? 18 : 14,
                      height: e.current ? 18 : 14,
                      borderRadius: e.current ? 9 : 7,
                      marginLeft: e.current ? -33 : -31,
                    },
                  ]}
                />
                <View style={{ flex: 1 }}>
                  <View style={st.tlHead}>
                    <Text
                      italic
                      style={{
                        fontFamily: theme.fontFamily.displayItalic,
                        fontStyle: 'italic',
                        fontSize: 56,
                        lineHeight: 56,
                        color: e.current ? theme.colors.accentAlt : theme.colors.fg,
                      }}
                    >
                      {e.year}
                    </Text>
                    {e.current ? <Pill variant="ember">{t('about.geschichte.pillCurrent')}</Pill> : null}
                  </View>
                  <Text variant="h3" style={{ marginTop: 12 }}>{e.title}</Text>
                  <Text variant="body" tone="fgSoft" style={{ marginTop: 8, maxWidth: 680 }}>{e.body}</Text>
                </View>
              </View>
            ))}
          </View>
        </Container>
      </View>

      {/* 03 Philosophie */}
      <View style={[st.philoBg, { backgroundColor: theme.colors.bgSoft }]}>
        <Container>
          <SectionHead
            idx={t('about.philosophie.idx')}
            title={t('about.philosophie.title')}
            italic={t('about.philosophie.italic')}
            sub={t('about.philosophie.sub')}
          />
          <View style={st.philoGrid}>
            {philosophie.map((p) => (
              <PhiloCard key={p.n} p={p} />
            ))}
          </View>

          <View style={[st.quote, { backgroundColor: theme.colors.fg, borderRadius: 12 }]}>
            <Text style={{ fontFamily: 'InstrumentSerif_400Regular_Italic', fontStyle: 'italic', fontSize: 80, lineHeight: 64, color: palette.emberSoft }}>
              «
            </Text>
            <Text
              italic
              style={{
                fontFamily: theme.fontFamily.displayItalic,
                fontStyle: 'italic',
                fontSize: 32,
                lineHeight: 42,
                color: palette.paper,
                marginTop: 8,
                maxWidth: 760,
              }}
            >
              {t('about.philosophie.quote')}
            </Text>
            <Text variant="meta" style={{ color: 'rgba(245,240,255,0.6)', marginTop: 24 }}>
              {t('about.philosophie.quoteAttribution')}
            </Text>
          </View>
        </Container>
      </View>

      {/* 04 Verein — Midnight */}
      <MoodSection
        mood="midnight"
        style={[
          st.midnight,
          Platform.OS === 'web'
            ? ({ boxShadow: `0 0 80px -20px ${palette.wine}55, 0 24px 60px -24px #15082E80` } as any)
            : null,
        ]}
      >
        <Container>
          <SectionHead
            idx={t('about.verein.idx')}
            title={t('about.verein.title')}
            italic={t('about.verein.italic')}
            sub={t('about.verein.sub')}
            invert
          />
          <View style={st.vereinWrap}>
            <View style={[st.memCard, { backgroundColor: 'rgba(245,240,255,0.06)', borderColor: 'rgba(245,240,255,0.16)', borderRadius: 12 }]}>
              <Pill variant="ember">{t('about.verein.memCard.pill')}</Pill>
              <Text
                style={{
                  fontFamily: 'InstrumentSerif_400Regular',
                  fontSize: 48,
                  lineHeight: 48,
                  color: palette.paper,
                  marginTop: 16,
                }}
              >
                {t('about.verein.memCard.titlePart1')}{' '}
                <Text style={{ fontFamily: 'InstrumentSerif_400Regular_Italic', fontStyle: 'italic', color: palette.emberSoft }}>
                  {t('about.verein.memCard.titleItalic')}
                </Text>
              </Text>
              <Text style={{ color: 'rgba(245,240,255,0.78)', fontSize: 15, lineHeight: 24, marginTop: 16, maxWidth: 520 }}>
                {t('about.verein.memCard.body')}
              </Text>
              <View style={st.memInfoRow}>
                <View style={[st.memInfo, { backgroundColor: 'rgba(245,240,255,0.10)', borderRadius: 8 }]}>
                  <Text variant="meta" style={{ color: palette.emberSoft }}>{t('about.verein.memCard.labelMembership')}</Text>
                  <Text style={{ fontFamily: 'InstrumentSerif_400Regular_Italic', fontStyle: 'italic', fontSize: 28, color: palette.paper, marginTop: 6 }}>
                    {verein.membership.price}
                  </Text>
                </View>
                <View style={[st.memInfo, { backgroundColor: 'rgba(245,240,255,0.10)', borderRadius: 8 }]}>
                  <Text variant="meta" style={{ color: palette.emberSoft }}>{t('about.verein.memCard.labelBenefit')}</Text>
                  <Text style={{ fontFamily: 'InstrumentSerif_400Regular_Italic', fontStyle: 'italic', fontSize: 28, color: palette.paper, marginTop: 6 }}>
                    {t('about.verein.memCard.valueBenefit')}
                  </Text>
                </View>
              </View>
              <View style={st.memActions}>
                <Button variant="ember" size="md" trailingArrow>{t('about.verein.memCard.ctaPrimary')}</Button>
                <Button variant="ghost" size="md">{t('about.verein.memCard.ctaSecondary')}</Button>
              </View>
            </View>
          </View>
        </Container>
      </MoodSection>

      {/* 05 Team */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('about.team.idx')}
            title={t('about.team.title')}
            italic={t('about.team.italic')}
            sub={t('about.team.sub')}
          />
          <TeamGrid teachers={teachers} />
        </Container>
      </View>

      {/* 06 Invite */}
      <View style={[st.inviteBg, { backgroundColor: theme.colors.bgSoft }]}>
        <Container>
          <View
            style={[
              st.invite,
              { backgroundColor: theme.colors.bg, borderRadius: 22, borderColor: theme.colors.rule },
            ]}
          >
            <View style={{ flex: 1.4, minWidth: 320 }}>
              <Text variant="meta" tone="fgMute">{t('about.invite.eyebrow')}</Text>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 56,
                  lineHeight: 56,
                  color: theme.colors.fg,
                  marginTop: 12,
                }}
              >
                {t('about.invite.titlePart1')}
                <Text italic style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: theme.colors.accent, fontSize: 56 }}>
                  {t('about.invite.titleItalic')}
                </Text>
              </Text>
              <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 16, maxWidth: 600 }}>
                {t('about.invite.body')}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 280, gap: 12 }}>
              <Button variant="primary" size="lg" trailingArrow href="/kurse">{t('about.invite.ctaPrimary')}</Button>
              <Button variant="ghost" size="lg" href="/kontakt">{t('about.invite.ctaSecondary')}</Button>
            </View>
          </View>
        </Container>
      </View>
    </View>
  );
}

function HeadPhotoCard({ theme }: { theme: ReturnType<typeof useTheme> }) {
  const { t } = useTranslation();
  const [size, setSize] = useState(0);
  return (
    <View
      style={st.headCardOuter}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width);
        if (w > 0 && w !== size) setSize(w);
      }}
    >
      <ImageBackground
        source={require('../../../assets/team/club-class.webp')}
        resizeMode="cover"
        style={[
          {
            width: '100%',
            height: size || undefined,
            backgroundColor: theme.colors.accent,
            borderRadius: 12,
            overflow: 'hidden',
            position: 'relative',
          },
          size === 0 && Platform.OS !== 'web' ? { aspectRatio: 1 } : null,
        ]}
        imageStyle={{ borderRadius: 12 }}
        accessibilityLabel={t('about.hero.photoCard.accessibilityLabel')}
      >
        <View style={st.headCardOverlay} />
        <View style={st.headCardContent}>
          <Text variant="meta" style={{ color: 'rgba(245,240,255,0.78)' }}>
            {t('about.hero.photoCard.caption')}
          </Text>
          <Text
            style={{
              fontFamily: 'InstrumentSerif_400Regular_Italic',
              fontStyle: 'italic',
              fontSize: fluidFontSize(64, 12, 88) as any,
              lineHeight: fluidLineHeight(1, 88) as any,
              color: palette.paper,
            }}
          >
            {t('about.hero.photoCard.year')}
          </Text>
          <Text variant="meta" style={{ color: 'rgba(245,240,255,0.85)' }}>
            {t('about.hero.photoCard.byline')}
          </Text>
        </View>
      </ImageBackground>
    </View>
  );
}

function PhiloCard({ p }: { p: PhilosophiePillar }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.philoCard,
        {
          backgroundColor: theme.colors.bg,
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
          borderRadius: 12,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <Text
        style={{
          fontFamily: theme.fontFamily.displayItalic,
          fontStyle: 'italic',
          fontSize: 56,
          lineHeight: 56,
          color: theme.colors.accent,
        }}
      >
        {p.n}
      </Text>
      <Text variant="h3" style={{ marginTop: 16 }}>{p.title}</Text>
      <Text variant="body" tone="fgSoft" style={{ marginTop: 12 }}>{p.body}</Text>
    </Pressable>
  );
}

const st = StyleSheet.create({
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 32 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  headGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 48, alignItems: 'flex-start' },
  headCardOuter: { flex: 1, minWidth: 280, alignSelf: 'flex-start' },
  headCardOverlay: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(12,8,20,0.35)' },
  headCardContent: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, padding: 24, justifyContent: 'space-between' },
  statRow: { marginTop: 56, flexDirection: 'row', flexWrap: 'wrap', borderTopWidth: 1, borderBottomWidth: 1 },
  stat: { flex: 1, minWidth: 200, paddingVertical: 24, paddingHorizontal: 24 },

  timeline: { paddingLeft: 24, borderLeftWidth: 1, gap: 48 },
  tlRow: { flexDirection: 'row', gap: 16 },
  tlBullet: { marginTop: 12, borderWidth: 4 },
  tlHead: { flexDirection: 'row', alignItems: 'center', gap: 16, flexWrap: 'wrap' },

  philoBg: { paddingVertical: 96 },
  philoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  philoCard: { flex: 1, minWidth: 260, padding: 32, borderWidth: 1 },
  quote: { marginTop: 48, padding: 48 },

  midnight: {
    paddingVertical: 96,
    paddingHorizontal: 8,
    marginHorizontal: 24,
    marginVertical: 48,
    borderRadius: 12,
    overflow: 'hidden',
  },
  vereinWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 32 },
  memCard: { flex: 1, minWidth: 320, padding: 40, borderWidth: 1 },
  memInfoRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 24 },
  memInfo: { flex: 1, minWidth: 160, padding: 16 },
  memActions: { marginTop: 24, flexDirection: 'row', flexWrap: 'wrap', gap: 12 },

  inviteBg: { paddingVertical: 96 },
  invite: { padding: 56, borderRadius: 22, borderWidth: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 48, alignItems: 'center' },
});
